import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import ChartHeader from "./ChartHeader";

function ChartNOK({ title, subtitle, data, analysisDays, detailsDesc, orderNumber = "", dateRange = ""  }) {
    // Przykładowe dane - w prawdziwej aplikacji byłyby przekazane jako props
    const defaultData = {
        NA: "n/a"
    };
    const inputData = data || defaultData;

    // Filtrujemy tylko te noki, które mają wartość > 0 i nie mainField
    const filteredEntries = Object.entries(inputData).filter(([key, value]) => key !== 'mainField' && value > 0);

    // Obliczamy sumę wszystkich wystąpień
    const totalCount = filteredEntries.reduce((sum, [key, value]) => sum + value, 0);

    // Przygotowujemy dane dla wykresu
    let chartData = filteredEntries.map(([name, count]) => ({
        name,
        value: count,
        percentage: ((count / totalCount) * 100).toFixed(1)
    }));

    // NOWE: Sortowanie - od największego % do najmniejszego
    chartData = chartData.sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage));

    // Kolory dla wykresu
    const colors = [
        '#FBCEB1',
        '#800080',
        '#FFFF00',
        '#808000',
        '#DC143C',
        '#C0C0C0',
        '#C2CC32',
        '#808080',
        '#20B2AA',
        '#C71585'
    ];

    // ✅ FONT OBSŁUGUJĄCY POLSKIE ZNAKI Z PEŁNYM WSPARCIEM BOLD - Segoe UI
    const fontStack = '"Segoe UI", "Helvetica", "Arial", sans-serif';

    // Custom tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '13px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    fontFamily: fontStack
                }}>
                    <p style={{ margin: 0, fontWeight: '600' }}>{`${data.name} : `}{
                        detailsDesc && detailsDesc
                            .find(el => el.label === data.name)
                            ?.value
                    }</p>
                    <p style={{ margin: 0, color: '#666' }}>{`Occurrences: ${data.value}`}</p>
                    <p style={{ margin: 0, color: '#666' }}>{`Percentage: ${data.percentage}%`}</p>
                </div>
            );
        }
        return null;
    };

    // Custom legend with scrollable container
    const CustomLegend = ({ payload }) => {
        return (
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '16px',
                marginTop: '16px',
                maxHeight: '120px',
                overflowY: 'auto',
                paddingRight: '8px',
                paddingBottom: '4px'
            }}>
                {payload.map((entry, index) => (
                    <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '13px',
                        fontFamily: fontStack,
                        flexBasis: 'auto',
                        whiteSpace: 'nowrap'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: entry.color,
                            marginRight: '6px',
                            borderRadius: '2px',
                            flexShrink: 0
                        }}></div>
                        <span style={{ color: 'rgb(52, 71, 103)', fontWeight: '600', fontFamily: fontStack }}>
                            {entry.value} : {entry.payload.value} ({entry.payload.percentage}%) {detailsDesc && detailsDesc
                            .find(el => el.label === entry.value)
                            ?.value}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    // Styles based on ContactContainer
    const styles = {
        container: {
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '24px',
            margin: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            fontFamily: fontStack
        },
        header: {
            marginBottom: '24px'
        },
        chartContainer: {
            backgroundColor: 'rgb(248, 249, 250)',
            borderRadius: '6px',
            padding: '24px',
            marginBottom: '28px',
            minHeight: '300px',
            display: 'flex',
            flexDirection: 'column'
        },
        statsContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            padding: '0 20px'
        },
        statItem: {
            textAlign: 'center'
        },
        statNumber: {
            fontSize: '20px',
            fontWeight: '700',
            color: 'rgb(52, 71, 103)',
            margin: '0',
            fontFamily: fontStack
        },
        statLabel: {
            fontSize: '13px',
            color: 'rgb(123, 128, 154)',
            margin: '0',
            fontFamily: fontStack
        }
    };

    return (
        <div style={styles.container}>
            <ChartHeader
                title={title}
                subtitle={subtitle}
                orderName={orderNumber}
                dateRange={dateRange}
            />

            <div style={styles.chartContainer}>
                <div style={styles.statsContainer}>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{analysisDays || 0}</p>
                        <p style={styles.statLabel}>Analysis Days</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{data && data.mainField || 0}</p>
                        <p style={styles.statLabel}>Total NOK</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{totalCount}</p>
                        <p style={styles.statLabel}>Total defects no.</p>
                    </div>
                </div>

                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            stroke="none"
                            dataKey="value"
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default ChartNOK;